import React, { useEffect, useMemo, useState } from 'react';
import { Search, Download, RefreshCw, LogOut, Loader2, Users, Inbox, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Logo } from '@/components/Logo';
import { ApplicantDetail } from '@/components/admin/ApplicantDetail';
import { TRACKS } from '@/data/flow';
import { formatIDR } from '@/lib/storage';
import { API } from '@/lib/api';

const trackLabel = (v) => TRACKS.find((t) => t.value === v)?.label || v || '—';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; }
};
const eduStr = (e) => (e?.level ? `${e.level} · ${e.major} · ${e.institution}${e.year ? ` (${e.year})` : ''}` : '—');

const csvEscape = (val) => {
  const s = val == null ? '' : String(val);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const AdminDashboard = ({ token, username, onLogout }) => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [trackFilter, setTrackFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = () => { setLoading(true); setReloadKey((k) => k + 1); };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API}/admin/applications`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) { if (active) onLogout(); return; }
        const data = await res.json();
        if (active) { setApps(data.applications || []); setError(null); }
      } catch (e) {
        if (active) setError('Gagal memuat data. Coba lagi.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token, onLogout, reloadKey]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return apps.filter((r) => {
      const a = r.answers || {};
      if (trackFilter !== 'all' && a.track !== trackFilter) return false;
      if (!q) return true;
      return [a.fullName, a.email, a.phone, a.city, r.id]
        .filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [apps, query, trackFilter]);

  const stats = useMemo(() => {
    const byTrack = {};
    apps.forEach((r) => { const t = r.answers?.track; byTrack[t] = (byTrack[t] || 0) + 1; });
    return byTrack;
  }, [apps]);

  const exportCSV = () => {
    const headers = ['ID', 'Nama', 'Email', 'WhatsApp', 'Domisili', 'Posisi/Tim', 'Pendidikan',
      'Skills', 'Pengetahuan bidang', 'Tes skill', 'Pengalaman', 'Portofolio', 'Instagram', 'LinkedIn', 'GitHub', 'X',
      'Motivasi', 'Durasi', 'Ekspektasi uang saku', 'Foto', 'Terverifikasi', 'Tanggal daftar'];
    const lines = [headers.join(',')];
    filtered.forEach((r) => {
      const a = r.answers || {};
      const s = a.social || {};
      const testStr = (a.skillTest || []).map((t, i) => `${i + 1}. ${t.question}\nJawab: ${t.answer}`).join('\n\n');
      const row = [
        r.id, a.fullName, a.email, a.phone, a.city, trackLabel(a.track), eduStr(a.education),
        (a.skills || []).join('; '), a.positionKnowledge, testStr, a.experience, (a.portfolio || []).join(' | '),
        s.instagram, s.linkedin, s.github, s.twitter, a.motivation, a.duration,
        a.salary === null || a.salary === undefined ? 'Fleksibel' : formatIDR(a.salary),
        r.photo?.url || '', r.meta?.turnstileVerified ? 'Ya' : 'Tidak', fmtDate(r.createdAt),
      ];
      lines.push(row.map(csvEscape).join(','));
    });
    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lamaran-indilabs-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-10 border-b border-border bg-card/80 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <Logo className="h-8" />
            <span className="hidden rounded-full bg-primary-soft px-3 py-1 text-xs font-semibold text-primary sm:inline">Dashboard HRD</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden text-sm text-muted-foreground sm:inline">Halo, {username || 'admin'}</span>
            <Button variant="ghost" size="sm" onClick={onLogout} className="text-muted-foreground hover:text-destructive">
              <LogOut className="h-4 w-4" /> Keluar
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        {/* stats */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <div className="rounded-2xl border border-border bg-card p-4 shadow-card">
            <div className="flex items-center gap-2 text-muted-foreground"><Users className="h-4 w-4 text-primary" /><span className="text-xs">Total pelamar</span></div>
            <p className="mt-1 font-display text-2xl font-bold text-foreground">{apps.length}</p>
          </div>
          {TRACKS.map((t) => (
            <div key={t.value} className="rounded-2xl border border-border bg-card p-4 shadow-card">
              <p className="truncate text-xs text-muted-foreground">{t.label}</p>
              <p className="mt-1 font-display text-2xl font-bold text-foreground">{stats[t.value] || 0}</p>
            </div>
          ))}
        </div>

        {/* toolbar */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex h-11 flex-1 items-center gap-2 rounded-xl border border-border bg-card px-3 focus-within:ring-2 focus-within:ring-primary/40">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cari nama, email, WhatsApp, atau ID..."
              className="h-full w-full min-w-0 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
          </div>
          <Select value={trackFilter} onValueChange={setTrackFilter}>
            <SelectTrigger className="h-11 w-full rounded-xl border-border bg-card sm:w-56"><SelectValue placeholder="Semua posisi" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua posisi</SelectItem>
              {TRACKS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outlineBrand" className="h-11 rounded-xl" onClick={reload} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Muat ulang
          </Button>
          <Button variant="brand" className="h-11 rounded-xl" onClick={exportCSV} disabled={filtered.length === 0}>
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </div>

        {/* table */}
        <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-card shadow-card">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-20 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" /> Memuat data...
            </div>
          ) : error ? (
            <div className="py-20 text-center text-destructive">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-20 text-muted-foreground">
              <Inbox className="h-10 w-10" />
              <p className="text-sm">{apps.length === 0 ? 'Belum ada lamaran masuk.' : 'Tidak ada hasil yang cocok.'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Nama</TableHead>
                    <TableHead>Posisi / Tim</TableHead>
                    <TableHead className="hidden lg:table-cell">Pendidikan</TableHead>
                    <TableHead className="hidden md:table-cell">WhatsApp</TableHead>
                    <TableHead className="hidden md:table-cell">Email</TableHead>
                    <TableHead className="hidden sm:table-cell">Tanggal daftar</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => {
                    const a = r.answers || {};
                    return (
                      <TableRow key={r.id} className="cursor-pointer" onClick={() => setSelected(r)}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {r.photo?.url ? (
                              <img src={r.photo.url} alt={a.fullName} className="h-9 w-9 rounded-lg object-cover ring-1 ring-border" />
                            ) : (
                              <span className="grid h-9 w-9 place-items-center rounded-lg bg-secondary text-sm font-semibold text-muted-foreground">{(a.fullName || '?').charAt(0)}</span>
                            )}
                            <div className="min-w-0">
                              <p className="truncate font-medium text-foreground">{a.fullName || '—'}</p>
                              <p className="font-mono text-xs text-muted-foreground">{r.id}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell><Badge className="rounded-full bg-primary-soft text-primary hover:bg-primary-soft">{trackLabel(a.track)}</Badge></TableCell>
                        <TableCell className="hidden max-w-[220px] truncate lg:table-cell text-muted-foreground">{eduStr(a.education)}</TableCell>
                        <TableCell className="hidden md:table-cell text-muted-foreground">{a.phone || '—'}</TableCell>
                        <TableCell className="hidden md:table-cell text-muted-foreground">{a.email || '—'}</TableCell>
                        <TableCell className="hidden sm:table-cell text-muted-foreground">{fmtDate(r.createdAt)}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" className="text-primary" onClick={(e) => { e.stopPropagation(); setSelected(r); }}>
                            <Eye className="h-4 w-4" /> Detail
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
        {!loading && filtered.length > 0 && (
          <p className="mt-3 text-xs text-muted-foreground">Menampilkan {filtered.length} dari {apps.length} pelamar.</p>
        )}
      </main>

      <ApplicantDetail record={selected} open={Boolean(selected)} onClose={() => setSelected(null)} />
    </div>
  );
};
